import { useQuery } from "@tanstack/react-query";
import { useRoute, useLocation } from "wouter";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Truck,
  User,
  Clock,
  FileText,
  Camera,
  MapPin,
  Package,
  Calendar,
  Phone,
  Mail,
  IdCard,
  Download,
  Eye
} from "lucide-react";

interface TransportationDetail {
  id: number;
  description: string;
  vehicleLPN: string;
  dateEntered: string;
  freightCompany: string;
  freightCustomer: string;
  transporter: string;
  status: string;
  reference: string;
  dateProcessed: string | null;
  dateDispatched: string | null;
  dateLeft: string | null;
  hoursInLot: string | null;
  driverName: string | null;
  driverIdNumber: string | null;
}

export default function TransportationDetailPage() {
  const [match, params] = useRoute("/transportation/:id");
  const [, setLocation] = useLocation();
  const transportationId = params?.id;

  // Fetch single transportation record
  const { data: transportationDetail, isLoading } = useQuery<TransportationDetail>({
    queryKey: ['/api/transportation', transportationId],
    queryFn: () => fetch(`/api/transportation/${transportationId}`).then(res => {
      if (!res.ok) throw new Error('Failed to fetch transportation details');
      return res.json();
    }),
    enabled: !!transportationId,
  });

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'left': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_lot': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'processing': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading transportation details...</p>
        </div>
      </div>
    );
  }

  if (!transportationDetail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Transportation Record Not Found</h2>
          <p className="text-gray-500 mb-6">The requested transportation record could not be found.</p>
          <Button onClick={() => setLocation('/transportation')} data-testid="button-back-to-list">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Transportation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => setLocation('/transportation')}
              data-testid="button-back"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2" data-testid="text-detail-title">
                <Truck className="w-8 h-8 text-blue-600" />
                Transportation Details
              </h1>
              <p className="text-gray-600 mt-1">
                {transportationDetail.description} - {transportationDetail.vehicleLPN}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="button-print">
              <Download className="w-4 h-4 mr-1" />
              Print
            </Button>
            <Badge className={getStatusColor(transportationDetail.status)} data-testid="badge-status">
              {transportationDetail.status?.toUpperCase()}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* 1. Order Details */}
            <Card data-testid="card-order-details">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  Order Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Description</label>
                    <p className="text-gray-900 font-medium" data-testid="text-description">{transportationDetail.description}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Reference Number</label>
                    <p className="text-gray-900 font-mono" data-testid="text-reference">{transportationDetail.reference}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Freight Company</label>
                    <p className="text-gray-900" data-testid="text-freight-company">{transportationDetail.freightCompany}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Freight Customer</label>
                    <p className="text-gray-900" data-testid="text-freight-customer">
                      {transportationDetail.freightCustomer && transportationDetail.freightCustomer !== "Unknown Supplier" ? transportationDetail.freightCustomer : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Transporter</label>
                    <p className="text-gray-900" data-testid="text-transporter">
                      {transportationDetail.transporter && transportationDetail.transporter !== "Unknown Transporter" ? transportationDetail.transporter : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Current Status</label>
                    <Badge className={getStatusColor(transportationDetail.status)} data-testid="badge-order-status">
                      {transportationDetail.status?.toUpperCase()}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. Vehicle Details */}
            <Card data-testid="card-vehicle-details">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-green-600" />
                  Vehicle Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">License Plate Number</label>
                    <p className="text-gray-900 font-mono font-bold text-lg" data-testid="text-vehicle-lpn">
                      {transportationDetail.vehicleLPN && transportationDetail.vehicleLPN !== "N/A" ? transportationDetail.vehicleLPN : "Not assigned"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Vehicle Type</label>
                    <p className="text-gray-900" data-testid="text-vehicle-type">Heavy Duty Truck</p>
                  </div>
                </div>
                
                {/* Number Plate Photos */}
                <Separator />
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Number Plate Recognition Photos
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Entry Camera</p>
                      <p className="text-xs text-gray-400 mt-1">No photo available</p>
                    </div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">Exit Camera</p>
                      <p className="text-xs text-gray-400 mt-1">No photo available</p>
                    </div>
                  </div>
                </div>

                {/* Additional Vehicle Photos */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Additional Vehicle Photos
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Camera className="w-8 h-8 text-gray-400 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">Photo {i}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 3. Driver Details */}
            <Card data-testid="card-driver-details">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-purple-600" />
                  Driver Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Driver Name</label>
                    <p className="text-gray-900 font-medium" data-testid="text-driver-name">
                      {transportationDetail.driverName && transportationDetail.driverName.trim() ? transportationDetail.driverName : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">ID Number</label>
                    <p className="text-gray-900 font-mono" data-testid="text-driver-id">
                      {transportationDetail.driverIdNumber && transportationDetail.driverIdNumber !== "N/A" ? transportationDetail.driverIdNumber : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Contact Number</label>
                    <p className="text-gray-900 flex items-center gap-1" data-testid="text-driver-phone">
                      <Phone className="w-4 h-4 text-gray-400" />
                      Not specified
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email Address</label>
                    <p className="text-gray-900 flex items-center gap-1" data-testid="text-driver-email">
                      <Mail className="w-4 h-4 text-gray-400" />
                      Not specified
                    </p>
                  </div>
                </div>

                {/* Driver Photo */}
                <Separator />
                <div>
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Driver Photo
                  </h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center max-w-xs">
                    <User className="w-16 h-16 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Driver Photo</p>
                    <p className="text-xs text-gray-400 mt-1">No photo available</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Times & Documents */}
          <div className="space-y-6">
            
            {/* 4. Times */}
            <Card data-testid="card-times">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-600" />
                  Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-blue-900">Date Entered</p>
                      <p className="text-xs text-blue-700" data-testid="text-date-entered">
                        {transportationDetail.dateEntered ? format(new Date(transportationDetail.dateEntered), "dd MMM yyyy, HH:mm") : "Not recorded"}
                      </p>
                    </div>
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>

                  <div className="flex justify-between items-center p-3 bg-yellow-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-yellow-900">Date Processed</p>
                      <p className="text-xs text-yellow-700" data-testid="text-date-processed">
                        {transportationDetail.dateProcessed ? format(new Date(transportationDetail.dateProcessed), "dd MMM yyyy, HH:mm") : "Not processed"}
                      </p>
                    </div>
                    <Clock className="w-4 h-4 text-yellow-600" />
                  </div>

                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-purple-900">Date Dispatched</p>
                      <p className="text-xs text-purple-700" data-testid="text-date-dispatched">
                        {transportationDetail.dateDispatched ? format(new Date(transportationDetail.dateDispatched), "dd MMM yyyy, HH:mm") : "Not dispatched"}
                      </p>
                    </div>
                    <MapPin className="w-4 h-4 text-purple-600" />
                  </div>

                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-green-900">Date Left</p>
                      <p className="text-xs text-green-700" data-testid="text-date-left">
                        {transportationDetail.dateLeft ? format(new Date(transportationDetail.dateLeft), "dd MMM yyyy, HH:mm") : "Still on site"}
                      </p>
                    </div>
                    <ArrowLeft className="w-4 h-4 text-green-600" />
                  </div>

                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-orange-900">Hours in Lot</p>
                      <p className="text-lg font-bold text-orange-700" data-testid="text-hours-lot">
                        {transportationDetail.hoursInLot && transportationDetail.hoursInLot.trim() ? transportationDetail.hoursInLot : "Calculating..."}
                      </p>
                    </div>
                    <Clock className="w-4 h-4 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 5. Documents */}
            <Card data-testid="card-documents">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-600" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  
                  {/* Weighbridge Documents */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      Weighbridge Documents
                    </h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-weighbridge-ticket">
                        <Download className="w-4 h-4 mr-2" />
                        Weighbridge Ticket
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-weight-certificate">
                        <Download className="w-4 h-4 mr-2" />
                        Weight Certificate
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Access Documents */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <IdCard className="w-4 h-4 text-green-600" />
                      Access Documents
                    </h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-entry-permit">
                        <Download className="w-4 h-4 mr-2" />
                        Entry Permit
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-gate-pass">
                        <Download className="w-4 h-4 mr-2" />
                        Gate Pass
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Load Documents */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <Package className="w-4 h-4 text-purple-600" />
                      Load Documents
                    </h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-delivery-note">
                        <Download className="w-4 h-4 mr-2" />
                        Delivery Note
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-manifest">
                        <Download className="w-4 h-4 mr-2" />
                        Load Manifest
                      </Button>
                    </div>
                  </div>

                  <Separator />

                  {/* Driver Documents */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-orange-600" />
                      Driver Documents
                    </h4>
                    <div className="space-y-2">
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-driver-license">
                        <Download className="w-4 h-4 mr-2" />
                        Driver's License
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-driver-id">
                        <Download className="w-4 h-4 mr-2" />
                        ID Document
                      </Button>
                      <Button variant="outline" size="sm" className="w-full justify-start" data-testid="button-passport">
                        <Download className="w-4 h-4 mr-2" />
                        Passport
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}